var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { Dimensions } from 'react-native';
import { Application } from '../../Application';
import { IComponentViewModelType } from './IComponentViewModel';
import { Observable } from '../../Data/Observable/Observable';
import { debounce } from '../../Helpers/Debounce/Debounce';
import { CoreConfig } from '../../CoreConfig';
const dimensions = new Observable({
    window: Dimensions.get('window'),
    screen: Dimensions.get('screen')
});
Dimensions.addEventListener('change', (value) => {
    dimensions.next(value);
});
export function component(options) {
    return function (Target) {
        class BindedComponent extends Component {
            constructor(props) {
                var _a;
                super(props);
                this.isFirstRender = true;
                this.prevDeps = this.getDeps(props);
                this.stateDimensions = {
                    screen: Object.assign({}, dimensions.value.screen),
                    window: Object.assign({}, dimensions.value.window)
                };
                this.localState = {
                    dependencies: undefined,
                    assets: null,
                    hasError: false,
                    localization: undefined
                };
                const coreProvider = Application.providers.loadProvider(CoreConfig.provider);
                this.provider = loadProvider();
                if (((_a = this.provider) === null || _a === void 0 ? void 0 : _a.canUseSyncResolve(Object.values(options.dependencies || {}))) && (coreProvider === null || coreProvider === void 0 ? void 0 : coreProvider.canUseSyncResolve())) {
                    this.loadDependenciesSync(coreProvider);
                    return;
                }
                this.loadDependanciesAsync(coreProvider);
            }
            update(value = {}) {
                this.localState = Object.assign(Object.assign({}, this.localState), value);
                this.isMountedComponent && this.forceUpdate();
                return this.localState;
            }
            componentDidMount() {
                this.isMountedComponent = true;
            }
            loadDependenciesSync(coreProvider) {
                var _a;
                this.viewModel = coreProvider === null || coreProvider === void 0 ? void 0 : coreProvider.resolveSync(IComponentViewModelType);
                const loadDeps = () => {
                    var _a;
                    const dependencies = (_a = this.viewModel) === null || _a === void 0 ? void 0 : _a.loadDependenciesSync({
                        options,
                        props: this.props,
                        provider: this.provider,
                        onUpdate: () => this.update()
                    });
                    const assetsObs = dependencies === null || dependencies === void 0 ? void 0 : dependencies.getAssetsUseCase.execute(options.moduleName || '');
                    const localizationObs = dependencies === null || dependencies === void 0 ? void 0 : dependencies.getLocalizationUseCase.execute(options.moduleName || '', options.componentName || '');
                    const configurationObs = dependencies === null || dependencies === void 0 ? void 0 : dependencies.getAppConfigurationUseCase.configuration;
                    this.update({
                        dependencies,
                        assets: assetsObs === null || assetsObs === void 0 ? void 0 : assetsObs.value,
                        localization: localizationObs === null || localizationObs === void 0 ? void 0 : localizationObs.value
                    });
                    let lastAssets = assetsObs === null || assetsObs === void 0 ? void 0 : assetsObs.value;
                    let lastLocalication = localizationObs === null || localizationObs === void 0 ? void 0 : localizationObs.value;
                    assetsObs === null || assetsObs === void 0 ? void 0 : assetsObs.observe(this, assets => {
                        if (lastAssets === assets) {
                            return;
                        }
                        lastAssets = assets;
                        this.update({ assets });
                    });
                    localizationObs === null || localizationObs === void 0 ? void 0 : localizationObs.observe(this, localization => {
                        if (lastLocalication === localization) {
                            return;
                        }
                        lastLocalication = localization;
                        this.update({ localization });
                    });
                    configurationObs === null || configurationObs === void 0 ? void 0 : configurationObs.observe(this, () => this.update());
                    const dimensionsUpdate = debounce((value) => {
                        const deps = [
                            this.stateDimensions.screen.height,
                            this.stateDimensions.screen.width,
                            this.stateDimensions.window.height,
                            this.stateDimensions.window.width
                        ];
                        const newDeps = [
                            value.screen.height,
                            value.screen.width,
                            value.window.height,
                            value.window.width
                        ];
                        if (deps.find((x, i) => x !== newDeps[i])) {
                            this.stateDimensions = {
                                screen: Object.assign({}, value.screen),
                                window: Object.assign({}, value.window)
                            };
                            this.update();
                        }
                    });
                    dimensions.observe(this, (value) => dimensionsUpdate(value));
                };
                (_a = this.viewModel) === null || _a === void 0 ? void 0 : _a.init(loadDeps);
            }
            loadDependanciesAsync(coreProvider) {
                var _a;
                return __awaiter(this, void 0, void 0, function* () {
                    yield (coreProvider === null || coreProvider === void 0 ? void 0 : coreProvider.loadDependencies());
                    yield ((_a = this.provider) === null || _a === void 0 ? void 0 : _a.loadDependencies(Object.values(options.dependencies || {})));
                    return this.loadDependenciesSync(coreProvider);
                });
            }
            componentWillUnmount() {
                var _a;
                this.isMountedComponent = false;
                this.timeout && clearTimeout(this.timeout);
                (_a = this.viewModel) === null || _a === void 0 ? void 0 : _a.destroyDependencies({
                    dependenciesMap: this.localState.dependencies || {},
                    provider: this.provider,
                    dimensionsSubscriber: this.dimensionsSubscriber
                });
            }
            componentDidCatch(error) {
                this.setState({ hasError: true });
                Application.onError.next({ type: 'JS', error });
            }
            shouldComponentUpdate(nextProps) {
                const deps = this.getDeps(nextProps);
                if (hasDiff(deps, this.prevDeps)) {
                    this.prevDeps = deps;
                    return true;
                }
                return false;
            }
            render() {
                if (!this.localState.dependencies || !this.localState.assets || !this.localState.localization || this.localState.hasError) {
                    return null;
                }
                const TargetComponent = Target;
                const props = Object.assign(Object.assign(Object.assign({}, this.localState.dependencies), this.props), { assets: this.localState.assets, localization: this.localState.localization, style: this.getStyles() });
                this.isFirstRender = false;
                return _jsx(TargetComponent, Object.assign({}, props));
            }
            getDeps(props) {
                const getArrayDeps = (values) => {
                    return values.reduce((acc, val) => {
                        if (Array.isArray(val)) {
                            return [...acc, ...getArrayDeps(val)];
                        }
                        acc.push(val);
                        return acc;
                    }, []);
                };
                return getArrayDeps(Object.values(props));
            }
            getStyles() {
                var _a, _b;
                if (this.isFirstRender) {
                    this.prevStyles = (_a = this.viewModel) === null || _a === void 0 ? void 0 : _a.getStyle(options, this.localState.dependencies, dimensions.value.window, Object.assign(Object.assign({}, this.localState.dependencies), this.props));
                }
                else {
                    const newStyle = (_b = this.viewModel) === null || _b === void 0 ? void 0 : _b.getStyle(options, this.localState.dependencies, dimensions.value.window, Object.assign(Object.assign({}, this.localState.dependencies), this.props));
                    if (hasDiff(Object.values(newStyle || {}), Object.values(this.prevStyles))) {
                        this.prevStyles = newStyle;
                    }
                }
                return this.prevStyles || {};
            }
        }
        BindedComponent.decoratorOptions = options;
        function hasDiff(firstDeps, secondDeps) {
            if (firstDeps.length !== secondDeps.length) {
                return true;
            }
            return firstDeps.findIndex((dep, i) => dep !== secondDeps[i]) > -1;
        }
        function loadProvider() {
            if (!options.provider) {
                return;
            }
            const provider = Application.providers.loadProvider(options.provider);
            if (!provider) {
                return;
            }
            return provider;
        }
        BindedComponent.displayName = options.componentName;
        return BindedComponent;
    };
}
